<template>
  <v-container fluid class="aml-dash pa-5">

    <!-- ══ Page loading skeleton ══ -->
    <template v-if="pageLoading">
      <div class="d-flex align-center justify-space-between mb-5">
        <v-skeleton-loader type="heading" width="220" />
        <v-skeleton-loader type="button" width="120" />
      </div>
      <v-row dense class="mb-5">
        <v-col v-for="i in 4" :key="i" cols="6" sm="3">
          <v-skeleton-loader type="card" height="90" />
        </v-col>
      </v-row>
      <v-skeleton-loader type="card" height="420" />
    </template>

    <template v-else>

      <!-- ══ Header ══ -->
      <div class="d-flex align-center justify-space-between mb-5">
        <div class="d-flex align-center">
          <button class="aml-back-btn mr-3" @click="$router.push({ name: 'watchlist-dashboard' })">
            <v-icon small>mdi-arrow-left</v-icon>
          </button>
          <div>
            <div class="aml-overline mb-1">Watchlist Screening</div>
            <h2 class="aml-title">All Cases</h2>
          </div>
        </div>
        <v-btn depressed small class="aml-btn-ghost" :loading="tableLoading" @click="loadCases">
          <v-icon small left>mdi-refresh</v-icon>Refresh
        </v-btn>
      </div>

      <!-- ══ Metric cards ══ -->
      <v-row dense class="mb-5">
        <v-col cols="6" sm="3">
          <div
            class="aml-metric-card"
            :class="{ 'aml-metric-active aml-metric-blue': tableFilter === 'active' }"
            @click="setTableFilter('active')"
          >
            <div class="aml-metric-label">Active</div>
            <div class="aml-metric-value" style="color:#185FA5">{{ metrics.active }}</div>
            <div class="aml-metric-sub">Open + Reopened</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div
            class="aml-metric-card"
            :class="{ 'aml-metric-active aml-metric-red': tableFilter === 'high' }"
            @click="setTableFilter('high')"
          >
            <div class="aml-metric-label">High priority</div>
            <div class="aml-metric-value" style="color:#E24B4A">{{ metrics.high }}</div>
            <div class="aml-metric-sub">Needs attention</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div
            class="aml-metric-card"
            :class="{ 'aml-metric-active aml-metric-purple': tableFilter === 'mine' }"
            @click="setTableFilter('mine')"
          >
            <div class="aml-metric-label">Assigned to me</div>
            <div class="aml-metric-value" style="color:#534AB7">{{ metrics.mine }}</div>
            <div class="aml-metric-sub">Your open cases</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div
            class="aml-metric-card"
            :class="{ 'aml-metric-active aml-metric-green': tableFilter === 'closed' }"
            @click="setTableFilter('closed')"
          >
            <div class="aml-metric-label">Closed</div>
            <div class="aml-metric-value" style="color:#3B6D11">{{ metrics.closed }}</div>
            <div class="aml-metric-sub">All time</div>
          </div>
        </v-col>
      </v-row>

      <!-- ══ Cases table ══ -->
      <div class="aml-card">

        <!-- Toolbar -->
        <div class="aml-toolbar">
          <div class="d-flex align-center" style="gap:6px;flex-wrap:wrap">
            <button
              v-for="tab in filterTabs"
              :key="tab.key"
              class="aml-filter-tab"
              :class="tableFilter === tab.key ? 'aml-filter-tab--active' : ''"
              :style="tableFilter === tab.key ? `border-color:${tab.color};background:${tab.color}18;color:${tab.color}` : ''"
              @click="setTableFilter(tab.key)"
            >
              {{ tab.label }}
              <span
                class="aml-filter-count"
                :style="tableFilter === tab.key ? `background:${tab.color}28;color:${tab.color}` : ''"
              >{{ tab.count }}</span>
            </button>

            <v-chip
              v-if="hasActiveFilters"
              x-small close outlined color="primary"
              class="ml-1"
              @click:close="clearFilters"
            >
              <v-icon x-small left>mdi-filter</v-icon>Filters active
            </v-chip>
          </div>

          <div class="d-flex align-center" style="gap:8px">
            <div class="aml-search-wrap">
              <v-icon x-small class="aml-search-icon">mdi-magnify</v-icon>
              <input
                v-model="tableSearch"
                class="aml-search-input"
                placeholder="Search cases..."
              />
            </div>
            <v-btn depressed x-small class="aml-btn-ghost" @click="filterDialog = true">
              <v-icon x-small left>mdi-filter-outline</v-icon>More filters
            </v-btn>
          </div>
        </div>

        <!-- Table -->
        <div class="aml-table-wrap">
          <table class="aml-table">
            <colgroup>
              <col style="width:14%">
              <col style="width:18%">
              <col style="width:12%">
              <col style="width:14%">
              <col style="width:9%">
              <col style="width:14%">
              <col style="width:11%">
              <col style="width:8%">
            </colgroup>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Subject</th>
                <th>Provider(s)</th>
                <th>Stage</th>
                <th>Priority</th>
                <th>Assigned to</th>
                <th>Opened</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="tableLoading">
                <td colspan="8">
                  <v-skeleton-loader type="table-row-divider@6" />
                </td>
              </tr>
              <tr v-else-if="!filteredCases.length">
                <td colspan="8" class="aml-empty-row">No cases match this filter</td>
              </tr>
              <tr
                v-for="item in filteredCases"
                :key="item.id"
                class="aml-table-row"
                @click="goToCase(item.id)"
              >
                <!-- Reference + status -->
                <td>
                  <div class="aml-ref-link">{{ item.caseReference }}</div>
                  <div class="d-flex align-center" style="gap:3px;margin-top:2px;flex-wrap:wrap">
                    <span class="aml-pill" :style="caseStatusStyle(item.status)" style="font-size:9px">
                      {{ caseStatusLabel(item.status) }}
                    </span>
                    <span
                      v-if="item.hasNewSearchActivity"
                      class="aml-pill"
                      style="background:#FFF4E5;color:#854F0B;font-size:9px"
                    >
                      <v-icon x-small>mdi-bell-outline</v-icon>
                    </span>
                    <span v-if="item.reopenCount > 0" class="aml-muted" style="font-size:9px">
                      ×{{ item.reopenCount }} reopened
                    </span>
                  </div>
                </td>

                <!-- Subject name + account -->
                <td>
                  <div style="font-size:12px;font-weight:500">{{ casePrimaryPerson(item) }}</div>
                  <div class="aml-muted" style="font-size:10px">{{ item.account && item.account.mainAccountNumber }}</div>
                </td>

                <!-- Provider(s) from linked flags -->
                <td>
                  <div class="d-flex flex-wrap" style="gap:3px">
                    <span
                      v-for="link in (item.watchlistFlags || []).slice(0, 3)"
                      :key="link.flagId || link.id"
                      class="aml-type-badge"
                      style="font-size:9px"
                    >
                      {{ link.flag ? link.flag.provider : (item.watchlistFlag && item.watchlistFlag.provider) }}
                    </span>
                    <span v-if="!item.watchlistFlags || !item.watchlistFlags.length" class="aml-type-badge" style="font-size:9px">
                      {{ item.watchlistFlag && item.watchlistFlag.provider }}
                    </span>
                  </div>
                </td>

                <!-- Stage -->
                <td>
                  <span class="aml-pill" :style="stageStyle(item)" style="font-size:10px">
                    {{ stageLabel(item) }}
                  </span>
                </td>

                <!-- Priority -->
                <td>
                  <span class="aml-pill" :style="priorityStyle(item.priority)" style="font-size:10px">
                    {{ item.priority }}
                  </span>
                </td>

                <!-- Assigned to -->
                <td>
                  <div v-if="item.assignedTo" class="d-flex align-center" style="gap:6px">
                    <div class="aml-avatar" :style="avatarStyle(assigneeName(item.assignedTo))">
                      {{ avatarInitials(assigneeName(item.assignedTo)) }}
                    </div>
                    <span class="aml-muted" style="font-size:11px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:90px">
                      {{ assigneeName(item.assignedTo) }}
                    </span>
                  </div>
                  <span v-else class="aml-unassigned">Unassigned</span>
                </td>

                <!-- Opened date -->
                <td class="aml-muted">{{ fmtDate(item.dateCreated) }}</td>

                <!-- Actions -->
                <td @click.stop>
                  <div class="d-flex align-center justify-end" style="gap:3px">
                    <v-tooltip bottom>
                      <template #activator="{ on }">
                        <button class="aml-icon-btn" v-on="on" @click="goToCase(item.id)">
                          <v-icon x-small color="#185FA5">mdi-eye</v-icon>
                        </button>
                      </template>
                      <span>View case</span>
                    </v-tooltip>
                    <v-tooltip v-if="!isClosedStatus(item.status) && item.assignedToUserId !== (loggedInUser && loggedInUser.id)" bottom>
                      <template #activator="{ on }">
                        <button class="aml-icon-btn" v-on="on" @click="assignToSelf(item)">
                          <v-icon x-small color="#1D9E75">mdi-account-check-outline</v-icon>
                        </button>
                      </template>
                      <span>Assign to me</span>
                    </v-tooltip>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Footer: pagination -->
        <div class="aml-table-footer">
          <span class="aml-muted">{{ filteredCases.length }} case{{ filteredCases.length !== 1 ? 's' : '' }}</span>
          <div class="d-flex align-center" style="gap:6px">
            <v-btn x-small text :disabled="page <= 1" @click="prevPage">
              <v-icon x-small>mdi-chevron-left</v-icon>Prev
            </v-btn>
            <span class="aml-muted" style="font-size:11px">Page {{ page }}</span>
            <v-btn x-small text :disabled="cases.length < limit" @click="nextPage">
              Next<v-icon x-small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

    </template>

    <!-- ══ Filter Dialog ══ -->
    <v-dialog v-model="filterDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-filter-outline</v-icon>
            <span class="subtitle-1">Filter cases</span>
          </div>
          <v-btn icon @click="filterDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            item-text="text" item-value="value"
            label="Status" clearable dense outlined
            prepend-inner-icon="mdi-list-status"
            class="mb-3"
          />
          <v-select
            v-model="filters.priority"
            :items="['HIGH', 'MEDIUM', 'LOW']"
            label="Priority" clearable dense outlined
            prepend-inner-icon="mdi-flag-outline"
            class="mb-3"
          />
          <v-text-field
            v-model="filters.accountNumber"
            label="Account number"
            clearable dense outlined
            prepend-inner-icon="mdi-card-account-details-outline"
            class="mb-3"
          />
          <v-text-field v-model="filters.dateFrom" label="Date from" type="date" dense outlined prepend-inner-icon="mdi-calendar-start" class="mb-3" />
          <v-text-field v-model="filters.dateTo"   label="Date to"   type="date" dense outlined prepend-inner-icon="mdi-calendar-end" />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn text color="grey darken-1" @click="clearFilters"><v-icon left small>mdi-close</v-icon>Clear</v-btn>
          <v-spacer />
          <v-btn text @click="filterDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" :loading="tableLoading" @click="applyFilters">
            <v-icon left small>mdi-magnify</v-icon>Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { mapState }         from 'vuex';
import PageMixin            from '@/mixins/page.mixin';
import FormatMixin          from '@/mixins/format.mixin';
import AmlMixin             from '@/mixins/aml.mixin';
import { WatchlistService }  from '@/modules/amlpro/services/watchlist.service';
import { AmlCaseService }    from '@/modules/amlpro/services/aml-case.service';

const svc     = new WatchlistService();
const caseSvc = new AmlCaseService();

const LIMIT = 30;

export default {
  name: 'WatchlistCases',
  mixins: [PageMixin, FormatMixin, AmlMixin],

  data() {
    return {
      pageLoading:  true,
      tableLoading: false,
      cases:        [],
      page:         1,
      limit:        LIMIT,
      tableFilter:  'active',
      tableSearch:  '',
      filterDialog: false,
      filters: {
        status:        null,
        priority:      null,
        accountNumber: '',
        dateFrom:      null,
        dateTo:        null,
      },
      statusOptions: [
        { text: 'Open',                    value: 'OPEN' },
        { text: 'Reopened',                value: 'REOPENED' },
        { text: 'Closed — Completed',      value: 'CLOSED_INVESTIGATION_COMPLETED' },
        { text: 'Closed — No action',      value: 'CLOSED_NO_ACTION' },
        { text: 'Closed — Report filed',   value: 'CLOSED_REPORT_FILED' },
      ],
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),

    metrics() {
      return {
        active: this.cases.filter(c => ['OPEN','REOPENED'].includes(c.status)).length,
        high:   this.cases.filter(c => c.priority === 'HIGH' && ['OPEN','REOPENED'].includes(c.status)).length,
        mine:   this.cases.filter(c => c.assignedToUserId === this.loggedInUser?.id && ['OPEN','REOPENED'].includes(c.status)).length,
        closed: this.cases.filter(c => this.isClosedStatus(c.status)).length,
      };
    },

    filterTabs() {
      return [
        { key: 'active', label: 'Active',       count: this.metrics.active, color: '#185FA5' },
        { key: 'high',   label: 'High priority', count: this.metrics.high,   color: '#E24B4A' },
        { key: 'mine',   label: 'Mine',          count: this.metrics.mine,   color: '#534AB7' },
        { key: 'closed', label: 'Closed',        count: this.metrics.closed, color: '#3B6D11' },
        { key: 'all',    label: 'All',           count: this.cases.length,   color: '#888' },
      ];
    },

    hasActiveFilters() {
      return !!(this.filters.status || this.filters.priority ||
                this.filters.accountNumber || this.filters.dateFrom || this.filters.dateTo);
    },

    displayedCases() {
      const f = this.tableFilter;
      if (f === 'active') return this.cases.filter(c => ['OPEN','REOPENED'].includes(c.status));
      if (f === 'high')   return this.cases.filter(c => c.priority === 'HIGH' && ['OPEN','REOPENED'].includes(c.status));
      if (f === 'mine')   return this.cases.filter(c => c.assignedToUserId === this.loggedInUser?.id && ['OPEN','REOPENED'].includes(c.status));
      if (f === 'closed') return this.cases.filter(c => this.isClosedStatus(c.status));
      return this.cases;
    },

    filteredCases() {
      let list = this.displayedCases;
      const q  = this.tableSearch.toLowerCase().trim();
      if (!q) return list;
      return list.filter(c =>
        c.caseReference?.toLowerCase().includes(q) ||
        c.account?.mainAccountNumber?.includes(q) ||
        this.casePrimaryPerson(c).toLowerCase().includes(q),
      );
    },
  },

  async mounted() {
    await this.loadCases();
    this.pageLoading = false;
  },

  methods: {
    async loadCases() {
      this.tableLoading = true;
      try {
        const params = { page: this.page, limit: this.limit };
        if (this.filters.status)        params.status        = this.filters.status;
        if (this.filters.priority)      params.priority      = this.filters.priority;
        if (this.filters.accountNumber) params.mainAccountNumber = this.filters.accountNumber;
        if (this.filters.dateFrom)      params.dateFrom      = this.filters.dateFrom;
        if (this.filters.dateTo)        params.dateTo        = this.filters.dateTo;

        const res    = await svc.fetchCases(params);
        const data   = res.data ?? res;
        this.cases   = data.data ?? data;
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load cases' });
      } finally {
        this.tableLoading = false;
      }
    },

    setTableFilter(f) {
      this.tableFilter = f;
      this.tableSearch = '';
    },

    applyFilters() {
      this.filterDialog = false;
      this.page = 1;
      this.loadCases();
    },

    clearFilters() {
      this.filters = { status: null, priority: null, accountNumber: '', dateFrom: null, dateTo: null };
      this.filterDialog = false;
      this.page = 1;
      this.loadCases();
    },

    prevPage() {
      if (this.page > 1) { this.page--; this.loadCases(); }
    },

    nextPage() {
      this.page++;
      this.loadCases();
    },

    async assignToSelf(item) {
      try {
        await svc.assignCase(item.id, this.loggedInUser.id);
        this.showSnack({ color: 'success', title: 'Assigned', message: `Case assigned to you` });
        this.loadCases();
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to assign' });
      }
    },

    goToCase(id) {
      this.$router.push({ name: 'watchlist-case-detail', params: { caseId: id } });
    },

    casePrimaryPerson(c) {
      const persons = c.account?.person ?? [];
      if (persons.length) {
        const p = persons[0]?.person;
        if (p) return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || c.account?.mainAccountNumber;
      }
      return c.account?.mainAccountNumber ?? '—';
    },

    caseStatusLabel(s) {
      const map = {
        OPEN:                           'Open',
        REOPENED:                       'Reopened',
        CLOSED_INVESTIGATION_COMPLETED: 'Completed',
        CLOSED_NO_ACTION:               'No action',
        CLOSED_REPORT_FILED:            'Report filed',
      };
      return map[s] ?? s;
    },

    caseStatusStyle(s) {
      const map = {
        OPEN:                           'background:#E6F1FB;color:#185FA5',
        REOPENED:                       'background:#FFF4E5;color:#854F0B',
        CLOSED_INVESTIGATION_COMPLETED: 'background:#EAF3DE;color:#3B6D11',
        CLOSED_NO_ACTION:               'background:#F1EFE8;color:#5F5E5A',
        CLOSED_REPORT_FILED:            'background:#EEEDFE;color:#534AB7',
      };
      return map[s] ?? 'background:#f5f5f5;color:#888';
    },

    stageLabel(item) {
      if (this.isClosedStatus(item.status)) return this.caseStatusLabel(item.status);
      return item.reportStage || 'Open';
    },

    stageStyle(item) {
      if (this.isClosedStatus(item.status)) return 'background:#EAF3DE;color:#3B6D11';
      return 'background:#E6F1FB;color:#185FA5';
    },

    priorityStyle(p) {
      const map = {
        HIGH:   'background:#FCEBEB;color:#A32D2D',
        MEDIUM: 'background:#FAEEDA;color:#854F0B',
        LOW:    'background:#EAF3DE;color:#3B6D11',
      };
      return map[p] ?? 'background:#f5f5f5;color:#888';
    },

    avatarInitials(name) {
      if (!name || name === '—') return '?';
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    },

    avatarStyle(name) {
      const palettes = [
        'background:#B5D4F4;color:#185FA5',
        'background:#9FE1CB;color:#0F6E56',
        'background:#CECBF6;color:#3C3489',
        'background:#F5C4B3;color:#993C1D',
        'background:#FAC775;color:#854F0B',
      ];
      const idx = name ? name.charCodeAt(0) % palettes.length : 0;
      return palettes[idx];
    },

    isClosedStatus(s) {
      return ['CLOSED_INVESTIGATION_COMPLETED','CLOSED_NO_ACTION','CLOSED_REPORT_FILED'].includes(s);
    },
  },
};
</script>

<style scoped>
.aml-dash         { background:transparent; }
.aml-overline     { font-size:11px; font-weight:600; letter-spacing:1px; text-transform:uppercase; color:#999; }
.aml-title        { font-size:22px; font-weight:500; letter-spacing:-0.3px; margin:0; }
.aml-back-btn     { width:34px; height:34px; border-radius:8px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.12s; flex-shrink:0; }
.aml-back-btn:hover { background:#f5f5f5; }
.aml-btn-ghost    { font-size:12px !important; border:0.5px solid #ddd !important; background:transparent !important; color:inherit !important; box-shadow:none !important; }
.aml-metric-card  { border:0.5px solid #e0e0e0; border-radius:12px; padding:16px 20px; cursor:pointer; transition:border-color 0.15s,background 0.15s; background:white; height:100%; }
.aml-metric-card:hover { background:#f7f9fc; }
.aml-metric-active { border-width:1.5px !important; }
.aml-metric-blue   { border-color:#378ADD !important; background:#378ADD12 !important; }
.aml-metric-red    { border-color:#E24B4A !important; background:#E24B4A12 !important; }
.aml-metric-purple { border-color:#534AB7 !important; background:#534AB712 !important; }
.aml-metric-green  { border-color:#3B6D11 !important; background:#3B6D1112 !important; }
.aml-metric-label  { font-size:11px; color:#777; margin-bottom:6px; font-weight:500; }
.aml-metric-value  { font-size:28px; font-weight:600; line-height:1; margin-bottom:6px; }
.aml-metric-sub    { font-size:11px; color:#aaa; }
.aml-card          { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; }
.aml-toolbar       { padding:12px 16px; border-bottom:0.5px solid #e0e0e0; display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
.aml-filter-tab    { font-size:11px; padding:4px 11px; border-radius:20px; border:0.5px solid #ddd; background:transparent; color:#666; cursor:pointer; transition:all 0.12s; display:inline-flex; align-items:center; gap:5px; white-space:nowrap; }
.aml-filter-tab:hover { background:#f5f5f5; }
.aml-filter-tab--active { font-weight:600; }
.aml-filter-count  { font-size:10px; font-weight:700; padding:1px 5px; border-radius:10px; background:#f5f5f5; color:#aaa; }
.aml-search-wrap   { position:relative; }
.aml-search-icon   { position:absolute; left:8px; top:50%; transform:translateY(-50%); color:#bbb; }
.aml-search-input  { font-size:12px; padding:5px 10px 5px 26px; border:0.5px solid #ddd; border-radius:8px; background:white; color:inherit; width:180px; outline:none; font-family:inherit; }
.aml-search-input:focus { border-color:#999; }
.aml-table-wrap    { overflow-x:auto; }
.aml-table         { width:100%; border-collapse:collapse; font-size:12px; table-layout:fixed; }
.aml-table thead tr { border-bottom:0.5px solid #e0e0e0; }
.aml-table th      { padding:9px 14px; text-align:left; font-size:10px; font-weight:600; color:#aaa; letter-spacing:.5px; text-transform:uppercase; }
.aml-table-row     { border-bottom:0.5px solid #e0e0e0; cursor:pointer; transition:background 0.1s; }
.aml-table-row:hover { background:#EBF3FC; }
.aml-table td      { padding:10px 14px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.aml-empty-row     { padding:32px 20px; text-align:center; color:#aaa; font-size:13px; }
.aml-table-footer  { padding:9px 16px; border-top:0.5px solid #e0e0e0; font-size:11px; color:#aaa; display:flex; align-items:center; justify-content:space-between; }
.aml-muted         { color:#777; }
.aml-ref-link      { color:#185FA5; font-weight:600; }
.aml-type-badge    { font-size:10px; font-weight:700; color:#666; background:#f5f5f5; padding:2px 6px; border-radius:4px; }
.aml-pill          { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:500; white-space:nowrap; }
.aml-unassigned    { font-size:11px; color:#aaa; font-style:italic; }
.aml-icon-btn      { width:24px; height:24px; border-radius:6px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; padding:0; transition:background 0.1s; }
.aml-icon-btn:hover { background:#f0f0f0; }
</style>